import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationMenu from '../../components/NavigationMenu/NavigationMenu';
import { getAdminDashboard } from '../../../services/DashboardService';
import { getDashboardMetrics } from '../../../services/ActivityService';
import './Dashboard.css';

const emptyDashboard = {
    stats: {},
    activity30Days: { count: 0 },
    transactions30Days: { total: 0, count: 0 },
    recentActivity: []
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(Number(value) || 0);
};

const formatActivityTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const activityIcon = (type) => {
    if (type === 'transaction' || type === 'invoice') return 'fa-solid fa-euro-sign';
    if (type === 'model') return 'fa-solid fa-user';
    if (type === 'user') return 'fa-solid fa-users';
    if (type === 'trash') return 'fa-solid fa-trash-can';
    if (type === 'contact' || type === 'casting' || type === 'fantasy') return 'fa-solid fa-inbox';
    return 'fa-solid fa-clock-rotate-left';
};

const Dashboard = () => {
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState(emptyDashboard);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const loadDashboard = async (showLoading = true) => {
        if (showLoading) setIsLoading(true);
        setError('');
        const persisted = getDashboardMetrics();
        try {
            const result = await getAdminDashboard();
            const apiStats = result.stats || {};
            const apiActivity = Array.isArray(result.recentActivity) ? result.recentActivity : [];
            const apiTransactions = result.transactions30Days || { total: 0, count: 0 };
            const hasPersistedActivity = persisted.activity30Days.count > 0;
            const hasPersistedTransactions = persisted.transactions30Days.count > 0;
            setDashboard({
                ...emptyDashboard,
                ...result,
                stats: {
                    ...apiStats,
                    totalUsers: apiStats.totalUsers ?? apiStats.users ?? apiStats.userCount ?? persisted.stats.totalUsers,
                    totalModels: apiStats.totalModels ?? apiStats.models ?? apiStats.modelCount ?? persisted.stats.totalModels,
                    activityCount30Days: hasPersistedActivity
                        ? persisted.activity30Days.count
                        : (apiStats.activityCount30Days ?? apiStats.activityTotal30Days ?? result.activity30Days?.count ?? apiActivity.length)
                },
                activity30Days: hasPersistedActivity ? persisted.activity30Days : (result.activity30Days || { count: 0 }),
                transactions30Days: hasPersistedTransactions ? persisted.transactions30Days : apiTransactions,
                recentActivity: persisted.recentActivity.length > 0 ? persisted.recentActivity : apiActivity
            });
        } catch (loadError) {
            // The local activity store keeps the dashboard useful in demo/offline mode.
            setDashboard(persisted);
            if (persisted.recentActivity.length === 0 && persisted.transactions30Days.count === 0) {
                setError(loadError instanceof TypeError ? 'Unable to connect to the dashboard service.' : loadError.message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadDashboard();
    }, []);

    const stats = dashboard.stats || {};
    const transactionSummary = dashboard.transactions30Days || {};
    const activitySummary = dashboard.activity30Days || {};
    const pendingCount = Number(stats.pendingFantasy || 0) + Number(stats.pendingCasting || 0) + Number(stats.pendingContact || 0);
    const statCards = [
        { label: 'Total Users', value: isLoading ? '…' : (stats.totalUsers ?? '—'), meta: 'Current user records', color: 'rgb(26, 31, 113)' },
        { label: 'Total Models', value: isLoading ? '…' : (stats.totalModels ?? '—'), meta: 'Current model records', color: 'rgb(26, 31, 113)' },
        { label: 'Activity · 30 Days', value: isLoading ? '…' : (stats.activityCount30Days ?? activitySummary.count ?? 0), meta: 'Recorded admin actions', color: '#59636e' },
        { label: 'Transactions · 30 Days', value: isLoading ? '…' : formatCurrency(transactionSummary.total ?? stats.transactionTotal30Days ?? 0), meta: `${transactionSummary.count ?? stats.transactionCount30Days ?? 0} recorded transactions`, color: '#1f883d' },
        { label: 'Pending Submissions', value: isLoading ? '…' : pendingCount, meta: 'Fantasy, casting, and contact', color: '#9a6700' }
    ];

    return (
        <div className="dashboard">
            <NavigationMenu />
            <div className="dashboard-content">
                <header className="dashboard-header">
                    <div className="header-left">
                        <h1 className="dashboard-title">Dashboard</h1>
                        <p className="dashboard-subtitle">Live agency activity and 30-day transaction totals</p>
                    </div>
                    <button type="button" className="dashboard-refresh" onClick={() => loadDashboard()} disabled={isLoading}>
                        {isLoading ? 'Loading...' : 'Refresh'}
                    </button>
                </header>
                <main className="dashboard-main">
                    {error && <div className="dashboard-feedback dashboard-error" role="alert">{error}</div>}

                    <div className="stats-grid">
                        {statCards.map(stat => (
                            <div key={stat.label} className="stat-card">
                                <div className="stat-header">
                                    <span className="stat-label">{stat.label}</span>
                                </div>
                                <div className="stat-value" style={{ color: stat.color }}>{stat.value}</div>
                                <p className="stat-meta">{stat.meta}</p>
                            </div>
                        ))}
                        <div className="stat-card quick-actions-card">
                            <div className="stat-header"><span className="stat-label">Quick Actions</span></div>
                            <div className="quick-actions-inline">
                                <button type="button" className="action-button-small primary" onClick={() => navigate('/users?action=create')}>Create User</button>
                                <button type="button" className="action-button-small outline" onClick={() => navigate('/models')}>View Models</button>
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-section">
                        <div className="section-header">
                            <div>
                                <h2 className="section-title">Recent Activity</h2>
                                <p className="section-caption">Persisted system actions, newest first</p>
                            </div>
                            <button type="button" className="section-button" onClick={() => loadDashboard()} disabled={isLoading}>Refresh</button>
                        </div>
                        <div className="activity-list">
                            {!isLoading && dashboard.recentActivity.length === 0 ? (
                                <div className="activity-empty">No activity has been recorded yet.</div>
                            ) : dashboard.recentActivity.map((activity, index) => (
                                <div key={activity.uuid || `${activity.action}-${index}`} className="activity-item">
                                    <div className="activity-icon"><i className={activityIcon(activity.type)}></i></div>
                                    <div className="activity-content">
                                        <div className="activity-action">{activity.action || 'System action'}</div>
                                        <div className="activity-meta">
                                            <span className="activity-user">{activity.userName || activity.userUuid || 'System'}</span>
                                            <span className="activity-time">{formatActivityTime(activity.time || activity.eventTime || activity.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Dashboard;
